import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import { DraftTab, formatRunDuration, ReviewBadge } from "../components/drafting";
import { ErrorState, Notice, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { keys, useDossier } from "../dossier";
import { formatDateTime, w } from "../i18n";
import { readinessLabel, readinessStatus, type UserStatus } from "../status";
import { ChapterDatapoints } from "./datapoints";

const BLOCK_STATUS: Record<string, UserStatus> = { produced: "generated", audit_ko: "needs_review", escalated: "missing",
  empty: "failed", validated: "approved" };

/** A whole chapter: its sections' readiness and last drafts, and the chapter run (LangGraph graph). */
export function ChapterPage() {
  const { code = "" } = useParams();
  const { dossier, readiness } = useDossier();
  const runs = useResource(keys.chapterRuns(dossier.id, code), () => api.chapterRuns(dossier.id, code),
    { pollMs: 3000 });
  const start = useMutation(api.startChapter);
  const reviews = useResource(keys.reviews(dossier.id), () => api.reviewSummary(dossier.id));
  const chapter = readiness.data?.chapters.find(item => item.code === code);
  const latest = runs.data?.items[0];
  const running = !!latest && ["queued", "running"].includes(latest.status);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  const started = Date.parse(latest?.started_at || latest?.created_at || "");
  const runningDuration = running && Number.isFinite(started) ? formatRunDuration(now - started) : null;

  const generate = async () => {
    if (await start.run(dossier.id, code)) {
      invalidate(keys.chapterRuns(dossier.id, code), keys.generationSummary(dossier.id), `generations:${dossier.id}`,
        `report-audits:${dossier.id}`, keys.audit(dossier.id), `review:${dossier.id}`, keys.reviews(dossier.id));
    }
  };
  if (readiness.loading) return <SkeletonBlock lines={8} />;
  if (!chapter) return <ErrorState title={w("chapter.notFound")} error={readiness.error} onRetry={readiness.reload} />;

  return <>
    <PageHeader eyebrow={w("nav.chapter", { code })} title={chapter.title} lead={w("chapter.lead")}
      actions={<button className="ws-btn primary" disabled={running || start.pending} onClick={generate}>
        {running ? w("chapter.running") : w("chapter.generate")}</button>} />
    {start.error && <ErrorState title={w("draft.startError")} error={start.error} />}
    {running && <Notice tone="info">{w("chapter.runningBody", { code, duration: runningDuration || "0 s" })}</Notice>}
    {latest?.status === "failed" && <ErrorState title={w("chapter.failed")} error={null} note={latest.error_message || ""} onRetry={generate} />}

    <section className="ws-card" style={{ marginBottom: 16 }}>
      <div className="ws-card-head"><h2>{w("chapter.sections")}</h2>
        {latest?.finished_at && <span className="ws-small ws-muted">{w("chapter.lastRun", { date: formatDateTime(latest.finished_at),
          duration: formatRunDuration(latest.duration_ms) || w("common.notAvailable") })}</span>}</div>
      <ul className="ws-list">{chapter.sections.map(section => {
        const outcome = latest?.summary?.[section.code];
        return <li key={section.code}>
          <Link className="ws-link" to={`/dossiers/${dossier.id}/sections/${section.code}`}><span className="ws-mono">{section.code}</span>&nbsp; {section.title}</Link>
          <span className="ws-row">
            <StatusBadge status={readinessStatus(section.status)} label={readinessLabel(section.status)} subtle />
            {outcome && <StatusBadge status={BLOCK_STATUS[outcome.block_status] || "not_started"}
              label={w(`draft.state.${outcome.block_status}`)} />}
            {reviews.data?.items[section.code] && <ReviewBadge status={reviews.data.items[section.code]} />}
            {reviews.data?.new_inputs.includes(section.code) && <span className="ws-tag" title={w("draft.newInputsTitle")}>{w("draft.newInputsTag")}</span>}
          </span>
        </li>;
      })}</ul>
    </section>

    <ChapterDatapoints chapter={code} />

    <h2 className="ws-section-title">{w("chapter.introduction")}</h2>
    <DraftTab code={code} />
    {runs.error && <ErrorState title={w("draft.loadError")} error={runs.error} onRetry={runs.reload} />}
  </>;
}
